import { normalizePortFailure, type PortError } from "../../ports/common/port-error";

export type CustomerDependencyErrorCode =
  | "DEPENDENCY_UNAVAILABLE"
  | "DEPENDENCY_TIMEOUT"
  | "DEPENDENCY_REJECTED"
  | "RATE_LIMITED"
  | "INTERNAL_ERROR";

export type CustomerReadQueryError = Readonly<{
  code: "FORBIDDEN" | "NOT_FOUND" | CustomerDependencyErrorCode;
}>;

export type CustomerDependencyError = Readonly<{
  code: CustomerDependencyErrorCode;
}>;

/**
 * Finite customer-facing normalization for owner-port failures.
 *
 * Owner authorization failures are not reinterpreted as Nave authorization;
 * they remain dependency rejection. NOT_FOUND may only be preserved by a
 * caller that has already performed its local anti-enumeration checks.
 */
export const normalizeCustomerReadPortError = (
  error: PortError,
  options: Readonly<{ preserveNotFound?: boolean }> = {},
): CustomerReadQueryError => {
  const normalized = normalizePortFailure(error);
  switch (normalized.category) {
    case "DEPENDENCY_UNAVAILABLE":
      return { code: "DEPENDENCY_UNAVAILABLE" };
    case "DEPENDENCY_TIMEOUT":
      return { code: "DEPENDENCY_TIMEOUT" };
    case "RATE_LIMITED":
      return { code: "RATE_LIMITED" };
    case "DEPENDENCY_REJECTED":
      return { code: "DEPENDENCY_REJECTED" };
    case "INTERNAL_ERROR":
      return { code: "INTERNAL_ERROR" };
    case "NOT_FOUND":
      return options.preserveNotFound === true
        ? { code: "NOT_FOUND" }
        : { code: "DEPENDENCY_REJECTED" };
    case "UNAUTHENTICATED":
    case "FORBIDDEN":
    case "VALIDATION_ERROR":
    case "CONFLICT":
      return { code: "DEPENDENCY_REJECTED" };
    default:
      return assertNever(normalized.category);
  }
};

export const normalizeCustomerDependencyPortError = (
  error: PortError,
): CustomerDependencyError => {
  const normalized = normalizeCustomerReadPortError(error);
  switch (normalized.code) {
    case "DEPENDENCY_UNAVAILABLE":
    case "DEPENDENCY_TIMEOUT":
    case "DEPENDENCY_REJECTED":
    case "RATE_LIMITED":
    case "INTERNAL_ERROR":
      return { code: normalized.code };
    case "FORBIDDEN":
    case "NOT_FOUND":
      return { code: "DEPENDENCY_REJECTED" };
    default:
      return assertNever(normalized.code);
  }
};

export const isCustomerDependencyErrorCode = (code: string): boolean =>
  code === "DEPENDENCY_UNAVAILABLE" ||
  code === "DEPENDENCY_TIMEOUT" ||
  code === "DEPENDENCY_REJECTED" ||
  code === "RATE_LIMITED" ||
  code === "INTERNAL_ERROR";

const assertNever = (value: never): never => {
  throw new Error(`Unhandled customer read error category: ${String(value)}`);
};
